import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { PackageCallout } from "@/components/PackageCallout";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";

// Images
import heroImage from "@/assets/driveways/paver-driveway-installation-cedar-grove-nj-2.webp";
import leoniaImage from "@/assets/bergen-county-driveway-paver-installation-leonia-nj.webp";
import paramusImage from "@/assets/driveways/paver-driveway-paramus-bergen-county-nj.webp";
import pomptonLakesImage from "@/assets/driveways/paver-driveway-installation-pompton-lakes-nj.webp";

// Citation link component - wraps text with citation number
const CitationLink = ({ num, url, children }: { num: number; url: string; children?: React.ReactNode }) => (
  <a 
    href={url} 
    target="_blank" 
    rel="noopener noreferrer" 
    className="text-secondary hover:underline"
  >
    {children}<sup>{num}</sup>
  </a>
);

const faqs = [
  {
    question: "How long does paver driveway installation take in North Jersey?",
    answer: "Most residential driveways take 3 to 7 days to complete. The timeline depends on the size of the driveway and the complexity of the design. Weather can also play a role, especially if we're working around rain in Bergen or Essex counties.",
  },
  {
    question: "Do I need a permit for a paver driveway in North Jersey?",
    answer: "It depends on your municipality. Some towns require a land use application for driveway work, and others may not. We recommend checking with your local building department before the project begins. We can help point you in the right direction.",
  },
  {
    question: "Can you install a paver driveway yourself?",
    answer: "You can, but we don't recommend it for driveways. The base must support the weight of vehicles, and that requires proper excavation, gravel depth, and compaction. Mistakes in the base layer often lead to sinking or shifting within the first year or two.",
  },
  {
    question: "Can you replace my asphalt or concrete driveway with pavers?",
    answer: "Yes. We remove the existing surface, prep the base properly, and install pavers in its place. It's a popular upgrade for homeowners who are tired of dealing with cracked concrete or deteriorating asphalt.",
  },
  {
    question: "Is it cheaper to lay concrete or pavers for a driveway?",
    answer: "Poured concrete may cost less upfront, but pavers offer advantages over time. Individual pavers can be replaced without tearing out the entire surface. Concrete cracks and often requires a full replacement. The long-term value of pavers tends to balance out the initial difference.",
  },
  {
    question: "How do I maintain my paver driveway after installation?",
    answer: "Periodic cleaning and topping off the joint sand as needed will keep things in good shape. Resanding with polymeric sand every 7 to 15 years helps maintain the surface, depending on wear and traffic. A pressure wash in the spring removes dirt and debris that builds up over winter.",
  },
  {
    question: "Should I hire an ICPI-certified contractor for my North Jersey driveway?",
    answer: "ICPI certification means the contractor has been trained on proper base preparation, drainage, and installation techniques. Driveways require a heavier base than patios or walkways, so working with a certified crew adds an extra level of confidence that the job is being done to standard.",
  },
];

const citations = [
  {
    num: 1,
    text: "Interlocking Concrete Pavement Institute (ICPI), Tech Spec 2: Construction of Interlocking Concrete Pavement – Base preparation standards for driveways.",
    url: "https://www.icpi.org",
  },
  {
    num: 2,
    text: "ASTM International, ASTM C936: Standard Specification for Solid Concrete Interlocking Paving Units – Paver compressive strength requirements (8,000 PSI minimum).",
    url: "https://www.astm.org",
  },
  {
    num: 3,
    text: "New Jersey Uniform Construction Code, Residential Code Table R301.2 – Frost line depth for Northern New Jersey.",
    url: "https://www.nj.gov/dca/divisions/codes",
  },
  {
    num: 4,
    text: "National Association of Realtors, 2023 Remodeling Impact Report – Outdoor hardscaping features and buyer interest.",
    url: "https://www.nar.realtor",
  },
  {
    num: 5,
    text: "ASCE/ICPI, Standard 58-16: Structural Design of Interlocking Concrete Pavement – Structural design standards for interlocking concrete pavement systems.",
    url: "https://www.icpi.org",
  },
];

const youtubeShortIds = ["xSJR_H3armE", "50WpbxLOZa8", "aJJQS6FX--0"];

export default function PaverDriveways() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Paver Driveway Installation Totowa, NJ | Masonry Pro</title>
        <meta name="description" content="Thinking about upgrading your driveway to pavers? Our North Jersey masonry team builds driveways with proper base work that holds up year after year here." />
        <meta property="og:description" content="Thinking about upgrading your driveway to pavers? Our North Jersey masonry team builds driveways with proper base work that holds up year after year here." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Paver Driveway Installation",
            description: "Expert paver driveway installation in North Jersey with proper base work that holds up year after year.",
            url: "/paving-services/paver-driveways",
            image: "https://masonry-pro.com/og/paver-driveways.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/paver-driveways.jpg" />
        <meta property="og:title" content="Paver Driveway Installation Totowa, NJ | Masonry Pro" />
        <meta name="twitter:title" content="Paver Driveway Installation Totowa, NJ | Masonry Pro" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/paver-driveways.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section - Centered with lighter overlay */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Paver driveway installation in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(13,16,23,0.45), rgba(13,16,23,0.65))' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Local Paver Driveway Installation in <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Totowa, NJ</span> Serving North Jersey
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.25rem', maxWidth: '48rem', margin: '0 auto 1.5rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Built to handle daily use and North Jersey freeze-thaw for years to come
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395">Schedule Your Free Assessment</a>
                </Button>
              </div>
            </div>
          </section>

          <PackageCallout packageId="driveway-upgrade" packageName="Driveway Upgrade Package" blurb={"Full paver driveway with a banded border and accent apron, sized for real vehicle loads so it holds up through North Jersey plow seasons."} />

          {/* Intro Section with Citation */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="prose prose-lg">
                  <p className="text-lg text-muted-foreground">
                    A paver driveway can handle years of daily traffic when it's built the right way. We install custom paver driveways for homeowners across <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> counties. Driveways take more wear than patios or walkways, so the base work matters even more. Our crews follow <CitationLink num={1} url="https://www.icpi.org">ICPI standards</CitationLink> on every project. The result is a driveway that looks great and holds up for decades.
                  </p>
                  <p className="text-lg text-muted-foreground mt-4">
                    Driveways here take a beating that patios never do, because you have vehicle weight, plow blades in the winter, and road salt tracking in from the street. On the hillier streets around Totowa and Hawthorne we also deal with runoff, which can wash out a base that was not built deep enough. That is why we run a thicker compacted base under a driveway than under a patio, and why we pay attention to where the water goes before we set a single paver. Watch a <Link to="/videos/paver-driveway-oakland-nj" className="text-secondary hover:underline">paver driveway we built in Oakland</Link>, including the matching walkway.
                  </p>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src={paramusImage}
                    alt="New paver driveway installed at a residential home with a two-car garage in Paramus, Bergen County, NJ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Heavy-Duty Base Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src={heroImage}
                    alt="Paver driveway installation in Cedar Grove, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    A Heavy-Duty Base Supports the <span className="text-gradient-gold">Weight of Vehicles</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Your driveway handles more daily wear than any other surface on your property. Every time you pull in or back out, the full weight of your vehicle sits on those pavers.
                    </p>
                    <p>
                      We use crushed gravel as the foundation, and we compact it in multiple layers. When the gravel is packed down properly, it locks together and distributes the load. Without having a good base, the pavers can shift, sink, or crack under the weight of a vehicle.
                    </p>
                    <p>
                      Some homeowners ask if they can skip the gravel and only use sand underneath. Sand compresses too easily on its own. It moves around, and over time the driveway won't stay level. Gravel gives you the strength that sand can't.
                    </p>
                    <p>
                      Additionally, clay soil is common across <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link> and <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link> counties, which can create additional challenges. We lay geotextile fabric beneath the gravel to keep clay from mixing in over time. <CitationLink num={1} url="https://www.icpi.org">ICPI has guidelines for driveway base depth based on soil type and vehicle load</CitationLink>. We follow those standards on every driveway we build.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Ground Preparation Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ground Preparation for Driveways Takes <span className="text-gradient-gold">Extra Attention</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    Driveway prep is more involved than a patio or walkway. The surface must support vehicles driving over it every day, so the ground beneath must be sound.
                  </p>
                  <p>
                    We begin by excavating the area to a depth appropriate for the soil conditions and drainage requirements. The ground is graded to direct water away from your home and off the driveway. Standing water on a driveway creates problems, especially in winter when it freezes.
                  </p>
                  <p>
                    We compact the base in layers, tamping each one down before adding the next. Rushing through this step is where a lot of driveways fail. Homeowners in Morristown and Wayne have called us to fix driveways that started sinking within a year or two. In most cases, the base wasn't compacted properly.
                  </p>
                  <p>
                    Every property we work on is a little different. Slope, soil type, and how many vehicles use the driveway all factor in. We evaluate the site before starting and adjust our prep work to match the conditions.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Year-Round Installation Section - Content LEFT, Image RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    We Install Paver Driveways <span className="text-gradient-gold">Year-Round</span> in North Jersey
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      We install driveways throughout the year. Each season has its own advantages, and we adjust our methods based on the conditions. The ground must be workable, and we plan accordingly.
                    </p>
                    <p>
                      Some homeowners in West Orange and Hackensack prefer to schedule their driveway installation before summer. Others book in the fall or winter when contractor schedules tend to open up. Both work well.
                    </p>
                    <p>
                      Polymeric sand sets best when temperatures remain between 50 and 80 degrees. When we're working outside that range, we adjust the timing of certain steps. Cold weather doesn't halt the project. It simply requires a different approach.
                    </p>
                    <p>
                      If you're in Paramus, Clifton, or Wayne and considering a new paver driveway, give us a call or text. We can discuss timing that works with your schedule.
                    </p>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl aspect-[4/3]">
                  <img
                    src={pomptonLakesImage}
                    alt="Paver driveway installation in Pompton Lakes, Passaic County, New Jersey"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Quality & Longevity Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Paver Driveways Last <span className="text-gradient-gold">Decades</span> with Professional Installation
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    A paver driveway is a long-term investment. When installed correctly, it can last 25 to 50 years. That's the kind of return homeowners in Livingston and Short Hills want to see.
                  </p>
                  <p>
                    Concrete pavers are manufactured to withstand heavy use. They meet <CitationLink num={2} url="https://www.astm.org">ASTM C936 standards with 8,000 PSI strength</CitationLink>. If one cracks in the years to come, it can be replaced without tearing out the entire driveway. That's an advantage asphalt and poured concrete simply don't offer.
                  </p>
                  <p>
                    <CitationLink num={5} url="https://www.icpi.org">Pavers interlock, so the weight of vehicles gets distributed across the surface</CitationLink>. This reduces stress on individual pavers and prevents cracking over time.
                  </p>
                  <p>
                    Our winters here in Northern New Jersey can take a toll on outdoor surfaces. Freeze-thaw cycles are relentless. Water seeps into small gaps, freezes, and then expands. That cycle breaks down poorly constructed driveways over time. Proper base work and drainage can prevent that from occurring. <CitationLink num={1} url="https://www.icpi.org">ICPI-certified installers</CitationLink> around Essex and Morris counties build with these conditions in mind.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Curb Appeal Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    A Paver Driveway Adds <span className="text-gradient-gold">Curb Appeal and Value</span> to Your Home
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      A paver driveway does more than handle daily traffic. It can add value to your property. <CitationLink num={4} url="https://www.nar.realtor">The National Association of Realtors puts outdoor hardscaping high on the list of features that buyers notice</CitationLink>.
                    </p>
                    <p>
                      Your driveway is one of the first things people see when they visit or drive by. A cracked asphalt surface sends one message, while a well-built paver driveway sends another entirely. In neighborhoods like Glen Ridge and Maplewood, that difference can matter.
                    </p>
                    <p>
                      Buyers shopping in Bergen and Essex counties often have outdoor upgrades on their checklist. A quality driveway signals that the property has been cared for, and that can make a real difference when it comes time to sell.
                    </p>
                    <p>
                      Your family benefits from it every day, too. Pulling into a driveway that looks great and holds up year after year is something you notice. That's value you can see, and value that holds over time.
                    </p>
                    <p>
                      A new paver driveway is a long-term investment in both function and curb appeal, and we make it easier to get started with financing through our partner Acorn Finance. Pre-qualification uses a soft credit check, so it will not affect your credit score. <Link to="/financing" className="text-secondary hover:underline">See your financing options</Link> and pick the monthly payment that fits.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src={leoniaImage}
                    alt="Newly installed paver driveway in Leonia, Bergen County, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Paver Driveway vs. Concrete Driveway Comparison Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Paver Driveway vs. Concrete Driveway: Which One Holds Up Better in <span className="text-gradient-gold">North Jersey</span>?
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    One question we hear quite a bit from homeowners in Bergen and Passaic counties is whether to choose a poured concrete driveway or a paver driveway. At first glance, both options look great, but the differences really don't show until after a few North Jersey winters. You know how brutal they can be.
                  </p>
                  <p>
                    Poured concrete driveways are made from a single slab. While they may look perfect when they are new, they will eventually start to crack due to our freeze-thaw cycles. When that happens, repairs become tricky. You can patch the cracks, but the new patches often don't blend in well with the original surface. If a crack runs deep or if the slab shifts, you might find yourself facing a costly full replacement.
                  </p>
                  <p>
                    On the flip side, paver driveways are constructed with individual interlocking pavers made of various materials. This design allows them to shift slightly with the ground underneath, which means they're less likely to crack. If one of the pavers chips or gets stained over the years, you simply replace that single unit, and the rest of the driveway will remain intact.
                  </p>
                  <p>
                    We know cost is also an important consideration. While a poured concrete driveway might be cheaper upfront, keep in mind that you'll need to reseal it every few years, and you might have to replace it entirely after 15 to 20 years. When you do the math, a well-built paver driveway, which can last 25 to 50 years with regular maintenance, becomes a lot more cost-effective over time.
                  </p>
                  <p>
                    Another thing to think about is the use of salt and ice melt in the winter. Many homeowners in Morris and Essex counties use rock salt or calcium chloride, which can cause concrete surfaces to pit and discolor faster than you might expect. Paver driveways tend to withstand these products better since the joints between the pavers allow for natural movement and expansion, reducing the risk of cracks.
                  </p>
                  <p>
                    At our company, we install both concrete driveways and paver driveways, and we understand that it can be a tough decision. If you're unsure about which option is right for your property, we'd be happy to help you go through the possibilities during a free on-site estimate.
                  </p>
                  <p>
                    Don't hesitate to give us a call or send a text at{" "}
                    <a href="tel:+19737252395" className="text-secondary hover:underline font-semibold">(973) 725-2395</a>. We're here to help!
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Common Mistakes Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Common Driveway Installation Mistakes We See <span className="text-gradient-gold">Across North Jersey</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    Contractors who rush through a driveway job tend to cut corners. We get calls from homeowners in Wayne, Paramus, and other towns asking us to fix driveways that weren't done right the first time.
                  </p>
                  <p>
                    The base is where most problems begin. Driveways require a stronger foundation than patios or walkways because of the vehicle weight. Without enough gravel beneath the surface, pavers start to sink. We've repaired driveways that were only a couple of seasons old because the base simply wasn't adequate.
                  </p>
                  <p>
                    Edge restraints are another area where some contractors cut back. Without them, pavers along the border spread apart over time. Gaps open up, and weeds find their way in.
                  </p>
                  <p>
                    Additionally, using the wrong sand in the joints creates problems. Regular sand washes out when it rains. Polymeric sand locks the pavers together and keeps weeds from taking root.
                  </p>
                  <p>
                    Skipping the geotextile fabric is common in Essex and Bergen counties. Clay soil is common throughout this region. Without a barrier, clay works its way into the gravel base and weakens it over time. <CitationLink num={1} url="https://www.icpi.org">ICPI-certified contractors</CitationLink> understand how to build for these conditions from the start.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Featured Snippet Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  How Long Do <span className="text-gradient-gold">Driveway Pavers</span> Usually Last?
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  A professionally installed paver driveway can last 25 to 50 years. In North New Jersey, our freeze-thaw cycles can take a toll on outdoor surfaces, so how the driveway is constructed makes a real difference.
                </p>
                <div className="bg-background/50 rounded-xl p-6 border border-border">
                  <h3 className="font-bold text-xl mb-4">Factors that affect how long your driveway will last:</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-3">
                      <span className="text-secondary font-bold">•</span>
                      <span>A properly compacted gravel base sized for your soil conditions and vehicle load, per <CitationLink num={1} url="https://www.icpi.org">ICPI standards</CitationLink></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-secondary font-bold">•</span>
                      <span>Polymeric sand in the joints to prevent erosion and weed growth</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-secondary font-bold">•</span>
                      <span>Periodic inspection and resanding with polymeric sand every 7 to 15 years, depending on wear and exposure</span>
                    </li>
                  </ul>
                  <p className="mt-6 text-muted-foreground">
                    When a qualified masonry contractor handles the base prep and drainage, your paver driveway can hold up for decades in our regional climate.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <Card key={index} className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-lg">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection 
            title="See Our Driveway"
            highlightedTitle="Transformations"
            videoIds={youtubeShortIds}
          />

          <RelatedServices currentCategory="paving" />

          {/* CTA Section */}
          <CTASection 
            heading="Need a Paver Driveway Installation?"
            serviceName="Paver Driveway"
            showFinancing
          />

          {/* Citations Section */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      Citations and References
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-3 text-sm text-muted-foreground">
                        {citations.map((citation) => (
                          <li key={citation.num} className="flex gap-2">
                            <span className="text-secondary font-bold">{citation.num}.</span>
                            <span>
                              {citation.text}{" "}
                              <a 
                                href={citation.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-secondary hover:underline"
                              >
                                {citation.url}
                              </a>
                            </span>
                          </li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>

          {/* Hub Backlink */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-lg text-muted-foreground">
                <p>
                  A paver driveway is one option among <Link to="/paving-services" className="text-secondary hover:underline">all our paving services</Link>, and we can compare it with concrete if you're deciding.
                </p>
              </div>
            </div>
          </section>

          {/* Footer CTA */}
          <section className="py-12 bg-gradient-to-r from-primary to-primary/80">
            <div className="container mx-auto px-4 text-center">
              <p className="text-lg md:text-xl text-primary-foreground mb-4">
                Thinking about upgrading your driveway to pavers? Our North Jersey masonry team builds driveways with proper base work that holds up year after year.
              </p>
              <Button variant="gold" size="lg" asChild>
                <a href="tel:+19737252395">Call or Text (973) 725-2395</a>
              </Button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
